import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { extractErrorMessage } from "@/lib/api";
import { useLang } from "@/i18n";
import { Plus, Trash2, Save, GripVertical, Archive, ArchiveRestore, AlertTriangle, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import LanguageTabs from "@/modules/questionnaire_admin/LanguageTabs";
import TranslationSummaryWidget from "@/modules/questionnaire_admin/TranslationSummaryWidget";
import StatusChip from "@/modules/questionnaire_admin/StatusChip";
import T1ConfirmReviewedDialog from "@/modules/questionnaire_admin/T1ConfirmReviewedDialog";
import BaseEditButton from "@/modules/questionnaire_admin/BaseEditButton";
import BaseEditDialog from "@/modules/questionnaire_admin/BaseEditDialog";
import ConflictDiffModal from "@/modules/questionnaire_admin/ConflictDiffModal";
import useI18nPatch from "@/modules/questionnaire_admin/useI18nPatch";
import { useAuth } from "@/contexts/AuthContext";

const QTYPES = [
  { v: "text", label: "Text" },
  { v: "textarea", label: "Long text" },
  { v: "number", label: "Number" },
  { v: "single_choice", label: "Single choice" },
  { v: "multi_choice", label: "Multi choice" },
  { v: "scale", label: "Scale 0-5" },
  { v: "boolean", label: "Yes / No" },
  { v: "email", label: "Email" },
  { v: "date", label: "Date" },
];

const SENSITIVE = ["health", "injury", "psychological", "wellbeing", "minor"];

// --- Same protection helpers as AdminQuestionnaires.jsx ---
// Demo classification short-circuits Officials to false so the protection
// guarantee survives even when a small (3–6) Official questionnaire exists.
const isDemoQ = (qq) => {
  const en = (qq?.title_en || "").toLowerCase().trim();
  const es = (qq?.title_es || "").toLowerCase().trim();
  if (en.includes("official") || es.includes("oficial")) return false;
  if (es.startsWith("encuesta piloto -") || es.startsWith("encuesta piloto-")) return true;
  if (["demo", "test", "seed", "pilot survey", "(copy)"].some((t) => en.includes(t))) return true;
  if (["demo", "test", "seed", "encuesta piloto", "(copy)"].some((t) => es.includes(t))) return true;
  const qc = Array.isArray(qq?.questions) ? qq.questions.length : (qq?.question_count ?? 0);
  if (qc >= 3 && qc <= 6) return true;
  return false;
};
const isOfficialQ = (qq) => {
  const en = (qq?.title_en || "").toLowerCase();
  const es = (qq?.title_es || "").toLowerCase();
  return en.includes("official") || es.includes("oficial");
};
const isProtectedInternalQ = (qq) => qq?.is_internal && !isDemoQ(qq);
const deleteBlockReasonKey = (qq, rc) => {
  if (isOfficialQ(qq)) return "admin.officialProtected";
  if (rc > 0) return "admin.cantDeleteWithResponses";
  if (qq?.is_active) return "admin.cantDeleteActive";
  if (isProtectedInternalQ(qq)) return "admin.cantDeleteInternal";
  return null;
};
const classificationKeyQ = (qq) => {
  if (isOfficialQ(qq)) return "admin.classificationOfficial";
  if (isDemoQ(qq)) return "admin.classificationDemo";
  if (qq?.is_internal) return "admin.classificationInternal";
  return "admin.classificationDefault";
};
const statusKeyQ = (qq) => {
  if (qq?.status === "archived") return "admin.statusArchived";
  if (qq?.is_active) return "admin.statusActive";
  return "admin.statusInactive";
};

const empty = {
  title_en: "", title_es: "", title_zh: "",
  description_en: "", description_es: "", description_zh: "",
  profile_keys: [], is_internal: false, is_active: true,
  questions: [],
};

// QUESTIONNAIRE-2.0-A-S1 — Active-language helpers.
// Mongo convention: hyphenated codes use underscores (pt-br → pt_br).
const normLang = (code) => (code || "en").replace("-", "_");
const fieldFor = (base, code) => `${base}_${normLang(code)}`;
const LANG_LOCALSTORAGE_KEY = "admin_q_builder_lang";
const ACTIVE_LANG_DEFAULT = "en";

// QUESTIONNAIRE-2.0-A-S2-β-B — Helper to build dotted-paths for PATCH /i18n.
// Mirrors the regex whitelist enforced server-side in S2-α §7.
const pathFor = ({ field, questionId, optionValue, lang }) => {
  const ln = normLang(lang);
  if (questionId && optionValue !== undefined && optionValue !== null) {
    return `questions.${questionId}.options.${optionValue}.label_${ln}`;
  }
  if (questionId) {
    return `questions.${questionId}.${field}_${ln}`;
  }
  return `${field}_${ln}`;
};

// Resolve the translation_status for a given dotted-path. Base langs (EN/ES/ZH)
// are implicit reviewed and have no entry in the map.
const statusFor = (ts, tsPath, langIsBase) => {
  if (langIsBase) return "reviewed";
  if (!ts) return null;
  return ts[tsPath] ?? null;
};

// QUESTIONNAIRE-2.0-A-S2-β-D — Extract the trailing lang code from a ts_path.
// Examples:
//   "title_ar"                              → "ar"
//   "description_pt_br"                     → "pt-br" (de-normalized via summary)
//   "questions.QID.label_fr"                → "fr"
//   "questions.QID.options.VAL.label_he"    → "he"
// Returns the canonical `language_code` from summary when available, else
// the de-normalized fallback (`_` → `-`).
const extractLangFromTsPath = (path, summaryLangs) => {
  if (!path) return null;
  const cleaned = String(path).replace(/^questions\.[^.]+\.(?:options\.[^.]+\.)?/, "");
  const m = cleaned.match(/^(title|description|label|help|section)_(.+)$/);
  if (!m) return null;
  const normLang = m[2];
  if (Array.isArray(summaryLangs)) {
    const direct = summaryLangs.find((L) => L.language_code === normLang);
    if (direct) return direct.language_code;
    const denorm = normLang.replace("_", "-");
    const indirect = summaryLangs.find((L) => L.language_code === denorm);
    if (indirect) return indirect.language_code;
  }
  return normLang.replace("_", "-");
};

const CASCADE_HIGHLIGHT_TTL_MS = 8000;

// QUESTIONNAIRE-2.0-A-S2-γ-D-B — Read the value stored at a translation_status
// `ts_path` from a freshly-loaded doc. Used to populate `remoteSnapshot.valueAtPath`
// when a 409 conflict opens the ConflictDiffModal. Pure, defensive — returns
// undefined on any mismatch so the modal can render the "remote not loaded" state.
const readValueAtPath = (doc, tsPath) => {
  if (!doc || !tsPath) return undefined;
  // Doc-level: title_<lang>, description_<lang>
  const docLevel = tsPath.match(/^(title|description)_/);
  if (docLevel && !tsPath.includes(".")) return doc[tsPath];
  // Question-level: questions.{qid}.(label|help|section)_<lang>
  const qm = tsPath.match(/^questions\.([^.]+)\.(label|help|section)_(.+)$/);
  if (qm) {
    const [, qid, field, lang] = qm;
    const q = (doc.questions || []).find((x) => x.id === qid);
    return q ? q[`${field}_${lang}`] : undefined;
  }
  // Option-level: questions.{qid}.options.{value}.label_<lang>
  const om = tsPath.match(/^questions\.([^.]+)\.options\.([^.]+)\.label_(.+)$/);
  if (om) {
    const [, qid, val, lang] = om;
    const q = (doc.questions || []).find((x) => x.id === qid);
    if (!q) return undefined;
    const opt = (q.options || []).find((o) => o.value === val);
    return opt ? opt[`label_${lang}`] : undefined;
  }
  return undefined;
};

const QuestionnaireBuilder = () => {
  const { id } = useParams();
  const isNew = !id || id === "new";
  const { t } = useLang();
  const navigate = useNavigate();
  const [q, setQ] = useState(empty);
  const [profiles, setProfiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [responseCount, setResponseCount] = useState(0);
  const [confirm, setConfirm] = useState(null); // 'delete' | 'archive' | 'restore'
  const [busy, setBusy] = useState(false);

  // QUESTIONNAIRE-2.0-A-S1 — i18n inline editor state.
  const [activeLang, setActiveLangState] = useState(() => {
    try {
      return localStorage.getItem(LANG_LOCALSTORAGE_KEY) || ACTIVE_LANG_DEFAULT;
    } catch { return ACTIVE_LANG_DEFAULT; }
  });
  const setActiveLang = (code) => {
    setActiveLangState(code);
    try { localStorage.setItem(LANG_LOCALSTORAGE_KEY, code); } catch { /* localStorage blocked */ }
  };
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState(null);
  const [showAllBase, setShowAllBase] = useState(false);

  const activeLangMeta = useMemo(() => {
    if (!summary) return null;
    return summary.languages.find((L) => L.language_code === activeLang) || null;
  }, [summary, activeLang]);
  const activeIsRtl = activeLangMeta?.direction === "rtl";
  const activeIsBase = activeLangMeta?.is_base ?? (activeLang === "en" || activeLang === "es" || activeLang === "zh");
  const activeHasScaffold = activeLangMeta?.has_scaffold ?? false;
  const activeLangBlockedNoScaffold = !activeIsBase && summary && !activeHasScaffold;

  const loadSummary = async () => {
    if (isNew || !id) return;
    setSummaryLoading(true); setSummaryError(null);
    try {
      const { data } = await api.get(`/admin/questionnaires/${id}/translation-summary`);
      setSummary(data);
    } catch (e) {
      setSummaryError({ status: e?.response?.status, detail: extractErrorMessage(e) });
    } finally {
      setSummaryLoading(false);
    }
  };

  // QUESTIONNAIRE-2.0-A-S2-β-B — wiring of PATCH /i18n via useI18nPatch hook.
  // QUESTIONNAIRE-2.0-A-S2-β-C — admin-only gating (defense in depth on top
  // of backend `role_required("admin")` which already returns 403).
  const { user } = useAuth();
  const userRole = user?.role || null;
  const canWriteI18n = userRole === "admin";
  const i18nReadOnlyReason = canWriteI18n
    ? null
    : userRole
      ? `Read-only: admin permission required (current role: ${userRole})`
      : "Read-only: not authenticated";

  const [t1Dialog, setT1Dialog] = useState(null);   // { mode, field, fromStatus, pendingPatch } | null
  const [t1Busy, setT1Busy] = useState(false);
  const [t1Error, setT1Error] = useState(null);
  // QUESTIONNAIRE-2.0-A-S2-γ-B — Base-edit dialog state: { path, currentValue } | null
  const [baseEditDialog, setBaseEditDialog] = useState(null);
  const [baseEditError, setBaseEditError] = useState(null);
  // QUESTIONNAIRE-2.0-A-S2-γ-D-B — Conflict modal state + last-attempt ref.
  // The ref captures the most recent value-attempt right before its PATCH is
  // submitted so `onConflict` can reconstruct the diff. Status-only flows
  // intentionally do NOT use this ref (γ-D-B scope = value flow only).
  const [conflict, setConflict] = useState(null); // { attempt, payload, remoteSnapshot, mode } | null
  const [conflictBusy, setConflictBusy] = useState(false);
  const lastAttemptRef = useRef(null);
  // QUESTIONNAIRE-2.0-A-S2-β-D — Track recent needs_update cascade so chips,
  // language tabs and the summary widget can highlight what just changed.
  const [recentCascade, setRecentCascade] = useState({ paths: new Set(), langs: new Set() });
  const i18nTs = q.translation_status || {};

  const reloadDocAfter409 = async () => {
    try {
      const { data } = await api.get(`/admin/questionnaires?include_archived=true`);
      const fresh = (data || []).find((x) => x.id === id);
      if (fresh) {
        setQ(fresh);
        setResponseCount(fresh.response_count ?? 0);
      }
      await loadSummary();
    } catch (_e) { /* best effort */ }
  };

  const { patchField, busy: patchBusy } = useI18nPatch({
    qid: id,
    getCurrentVersion: () => q.version || 1,
    onSuccess: async (resp) => {
      // Sync local doc: version, updated_at, last_edited_by + translation_status
      // (server may have triggered needs_update cascades).
      const fresh = await api.get(`/admin/questionnaires?include_archived=true`);
      const found = (fresh.data || []).find((x) => x.id === id);
      if (found) setQ(found);
      await loadSummary();
      const triggered = Array.isArray(resp?.needs_update_triggered) ? resp.needs_update_triggered : [];
      if (triggered.length > 0) {
        // QUESTIONNAIRE-2.0-A-S2-β-D — Cascade UX: highlight affected paths
        // (chips) and languages (tabs + summary rows) for CASCADE_HIGHLIGHT_TTL_MS.
        const pathsArr = triggered.map((x) => x.path).filter(Boolean);
        const summaryLangs = summary?.languages || [];
        const langsArr = pathsArr
          .map((p) => extractLangFromTsPath(p, summaryLangs))
          .filter(Boolean);
        const uniqLangs = Array.from(new Set(langsArr));
        setRecentCascade({ paths: new Set(pathsArr), langs: new Set(uniqLangs) });
        setTimeout(() => setRecentCascade({ paths: new Set(), langs: new Set() }), CASCADE_HIGHLIGHT_TTL_MS);
        const preview = uniqLangs.slice(0, 5).map((l) => l.toUpperCase()).join(", ");
        const more = uniqLangs.length > 5 ? ` +${uniqLangs.length - 5} more` : "";
        toast.warning("Base text updated — translations need review", {
          description: `${triggered.length} reviewed translation(s) marked as needs_update in: ${preview}${more}. Switch to those language tabs to review.`,
          duration: 8000,
        });
      } else {
        toast.success("i18n change saved.");
      }
    },
    onConflict: async (err) => {
      // QUESTIONNAIRE-2.0-A-S2-γ-D-B/C — Route to ConflictDiffModal based on
      // the captured attempt's shape:
      //   - status !== undefined  → mode="status" (StatusChip / T1).
      //   - only value !== undef  → mode="value"  (BaseEditDialog).
      //   - no attempt captured   → legacy toast+reload fallback.
      const attempt = lastAttemptRef.current;
      const isStatusAttempt = attempt && attempt.status !== undefined;
      const isValueAttempt = attempt && attempt.value !== undefined && !isStatusAttempt;
      if (isStatusAttempt || isValueAttempt) {
        let remoteSnapshot = {
          version: err?.currentVersion,
          valueAtPath: undefined,
          statusAtPath: undefined,
          lastEditedBy: undefined,
          updatedAt: undefined,
        };
        try {
          const { data } = await api.get(`/admin/questionnaires?include_archived=true`);
          const fresh = (data || []).find((x) => x.id === id);
          if (fresh) {
            setQ(fresh);
            setResponseCount(fresh.response_count ?? 0);
            await loadSummary();
            remoteSnapshot = {
              version: fresh.version,
              valueAtPath: readValueAtPath(fresh, attempt.path),
              statusAtPath: (fresh.translation_status || {})[attempt.path],
              lastEditedBy: fresh.last_edited_by || fresh.last_edited_by_email || undefined,
              updatedAt: fresh.updated_at || undefined,
            };
          }
        } catch (_e) { /* best effort */ }
        // Close T1 dialog if open — modal owns the conflict UX in status mode.
        if (isStatusAttempt && t1Dialog) {
          setT1Dialog(null);
          setT1Error(null);
        }
        setConflict({
          attempt,
          payload: err?.raw || {
            error: err?.code || "errors.version_conflict",
            current_version: err?.currentVersion,
            expected_version: err?.expectedVersion,
          },
          remoteSnapshot,
          mode: isStatusAttempt ? "status" : "value",
        });
        // Clear inline errors in BaseEditDialog / T1; modal owns UX now.
        setBaseEditError(null);
        return;
      }
      // Legacy fallback for unattempted patches.
      toast.error("Version conflict — another admin saved changes. Reloading…", { duration: 5000 });
      await reloadDocAfter409();
    },
    onValidationError: (err) => {
      const detail = err?.raw?.error || err?.code || "validation_error";
      toast.error(`Validation: ${detail}`, { duration: 5000 });
    },
    onUnauthorized: () => {
      toast.error("Forbidden — admin role required for i18n PATCH.", { duration: 5000 });
    },
    onNotFound: () => {
      toast.error("Questionnaire not found or archived.", { duration: 5000 });
    },
  });

  /** Compose a chip click → either PATCH directly or open T1 dialog first.
   *
   *  QUESTIONNAIRE-2.0-A-S2-β-C: hard-gate at the UI layer. Backend already
   *  returns 403 for non-admin roles but blocking here avoids opening the
   *  T1 dialog only to fail at the PATCH.
   */
  const handleChipClick = async ({ from, to, requiresT1, path }, ctx) => {
    if (isNew || patchBusy) return;
    if (!canWriteI18n) {
      toast.error(i18nReadOnlyReason || "Read-only", { duration: 4000 });
      return;
    }
    if (requiresT1) {
      setT1Error(null);
      setT1Dialog({
        mode: to === "reviewed" ? "promote" : "demote",
        field: { path, langDisplay: ctx.langDisplay, value: ctx.value },
        fromStatus: from,
        pendingPatch: { path, value: ctx.value, status: to, expected_previous_status: from || "" },
      });
      return;
    }
    try {
      // QUESTIONNAIRE-2.0-A-S2-γ-D-C — Capture status attempt for ConflictDiffModal.
      lastAttemptRef.current = { path, status: to, expected_previous_status: from || "" };
      await patchField({ path, status: to, expected_previous_status: from || "" });
      lastAttemptRef.current = null;
    } catch (_e) { /* handlers already toasted */ }
  };

  const handleT1Confirm = async () => {
    if (!t1Dialog) return;
    if (!canWriteI18n) {
      setT1Error(i18nReadOnlyReason || "Read-only");
      return;
    }
    setT1Busy(true); setT1Error(null);
    try {
      // QUESTIONNAIRE-2.0-A-S2-γ-D-C — Capture T1 attempt for ConflictDiffModal.
      // T1 sends value+status; the modal classifies as status mode because the
      // semantic action is the status transition, not a value PATCH.
      const pp = t1Dialog.pendingPatch;
      lastAttemptRef.current = {
        path: pp.path,
        status: pp.status,
        expected_previous_status: pp.expected_previous_status,
        // value is included for backend reviewed_requires_value check, but
        // diff mode is "status" since `status` is present.
        value: pp.value,
      };
      await patchField(pp);
      setT1Dialog(null);
      lastAttemptRef.current = null;
    } catch (e) {
      // 409 is owned by onConflict (it will close T1 itself if status mode).
      if (e?.status !== 409) {
        setT1Error(e?.raw?.error || e?.code || "patch_failed");
      }
    } finally {
      setT1Busy(false);
    }
  };

  // QUESTIONNAIRE-2.0-A-S2-γ-B — Base-edit handlers.
  // Opens the BaseEditDialog with the field's current value, runs the PATCH
  // through the existing useI18nPatch hook (whose onSuccess from β-D handles
  // toast cascade + summary refresh + recentCascade highlights), and closes
  // the dialog on success. Errors surface inline.
  const openBaseEdit = (path, currentValue) => {
    if (!canWriteI18n) return;
    setBaseEditError(null);
    setBaseEditDialog({ path, currentValue: currentValue || "" });
  };

  const closeBaseEdit = () => {
    setBaseEditDialog(null);
    setBaseEditError(null);
  };

  const handleBaseEditConfirm = async ({ path, value }) => {
    if (!canWriteI18n) {
      setBaseEditError(i18nReadOnlyReason || "Read-only");
      return;
    }
    setBaseEditError(null);
    // QUESTIONNAIRE-2.0-A-S2-γ-D-B — Capture attempt BEFORE patch so onConflict
    // can build the diff. `cachedRemoteValue` is what the admin saw when they
    // opened the BaseEditDialog (immutable for this session).
    lastAttemptRef.current = {
      path,
      value,
      cachedRemoteValue: baseEditDialog?.currentValue ?? "",
    };
    try {
      await patchField({ path, value });
      setBaseEditDialog(null);
      lastAttemptRef.current = null;
    } catch (e) {
      // 409 is handled by onConflict (opens ConflictDiffModal); keep
      // BaseEditDialog open silently so admin still sees their attempt.
      if (e?.status === 409) {
        setBaseEditError(null);
        return;
      }
      const code = e?.raw?.error || e?.code || "patch_failed";
      if (e?.status === 422) {
        setBaseEditError(`Validation: ${code}`);
      } else if (e?.status === 401 || e?.status === 403) {
        setBaseEditError("Not authorized to edit base text.");
      } else {
        setBaseEditError(`Save failed: ${code}`);
      }
    }
  };

  // QUESTIONNAIRE-2.0-A-S2-γ-D-B — Conflict modal handlers.
  // The modal is mounted whenever `conflict !== null`. Actions:
  //   - Reload remote: re-fetch doc and refresh `remoteSnapshot.valueAtPath`.
  //   - Cancel: close modal, keep BaseEditDialog open so admin can adjust.
  //   - Copy attempt: copy attempted value to clipboard (best-effort).
  //   - Discard: close modal + close BaseEditDialog + reload doc.
  //   - Retry: re-submit the same PATCH (now with refreshed if_version since
  //     `q.version` was already updated by the reload triggered in onConflict).
  const handleConflictReload = async () => {
    if (!conflict) return;
    setConflictBusy(true);
    try {
      const { data } = await api.get(`/admin/questionnaires?include_archived=true`);
      const fresh = (data || []).find((x) => x.id === id);
      if (fresh) {
        setQ(fresh);
        setResponseCount(fresh.response_count ?? 0);
        await loadSummary();
        setConflict((prev) => prev && {
          ...prev,
          remoteSnapshot: {
            ...prev.remoteSnapshot,
            version: fresh.version,
            valueAtPath: readValueAtPath(fresh, prev.attempt.path),
            statusAtPath: (fresh.translation_status || {})[prev.attempt.path],
            lastEditedBy: fresh.last_edited_by || fresh.last_edited_by_email || undefined,
            updatedAt: fresh.updated_at || undefined,
          },
        });
      }
    } catch (_e) { /* best effort */ }
    setConflictBusy(false);
  };

  const handleConflictCancel = () => {
    setConflict(null);
    // Keep lastAttemptRef so a subsequent retry from the same BaseEditDialog
    // session can reuse the cachedRemoteValue if needed.
  };

  const handleConflictCopyAttempt = async () => {
    if (!conflict?.attempt?.value) return;
    try {
      await navigator.clipboard.writeText(conflict.attempt.value);
      toast.success("Attempt copied to clipboard", { duration: 3000 });
    } catch (_e) {
      toast.error("Clipboard unavailable — please copy the text manually from the modal.");
    }
  };

  const handleConflictDiscard = async () => {
    setConflict(null);
    setBaseEditDialog(null);
    lastAttemptRef.current = null;
    await reloadDocAfter409();
    toast.message("Local change discarded.", { duration: 3000 });
  };

  // QUESTIONNAIRE-2.0-A-S2-γ-D-D — Auto-refresh remote snapshot every 30s
  // while the ConflictDiffModal is open (value mode only). Non-aggressive:
  // single interval, paused while a manual reload/retry is busy. Cleared
  // automatically when conflict closes. Does NOT touch `attempt` or trigger
  // any PATCH — only refreshes `remoteSnapshot.valueAtPath`.
  useEffect(() => {
    if (!conflict || conflict.mode !== "value") return undefined;
    const handle = setInterval(() => {
      if (!conflictBusy && !patchBusy) {
        handleConflictReload();
      }
    }, 30000);
    return () => clearInterval(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conflict, conflictBusy, patchBusy]);

  const handleConflictRetry = async () => {
    if (!conflict) return;
    const att = conflict.attempt;
    const refreshedCached = conflict.remoteSnapshot?.valueAtPath ?? att.cachedRemoteValue ?? "";
    setConflict(null);
    setConflictBusy(true);
    try {
      // Re-submit with refreshed if_version (q.version was already updated by
      // the reload that opened the modal). Update cachedRemoteValue too so a
      // subsequent 409 reclassifies correctly.
      lastAttemptRef.current = { ...att, cachedRemoteValue: refreshedCached };
      await patchField({ path: att.path, value: att.value });
      setBaseEditDialog(null);
      lastAttemptRef.current = null;
    } catch (_e) {
      // If retry triggers another 409, onConflict will reopen the modal.
    } finally {
      setConflictBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api.get("/profiles").then((r) => { if (!cancelled) setProfiles(r.data); });
    if (!isNew) {
      // include archived so the editor can also load archived questionnaires
      // for restore review without 404'ing.
      api.get(`/admin/questionnaires?include_archived=true`).then((r) => {
        if (cancelled) return;
        const found = (r.data || []).find((x) => x.id === id);
        if (found) {
          setQ(found);
          setResponseCount(found.response_count ?? 0);
        }
      });
      // Fire-and-forget translation summary load; reads only.
      loadSummary();
    }
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isNew]);

  const update = (patch) => setQ((prev) => ({ ...prev, ...patch }));
  const updateQuestion = (idx, patch) => setQ((prev) => ({
    ...prev,
    questions: prev.questions.map((qq, i) => i === idx ? { ...qq, ...patch } : qq),
  }));
  const addQuestion = () => setQ((prev) => ({
    ...prev,
    questions: [...prev.questions, {
      id: crypto.randomUUID(),
      type: "text",
      label_en: "",
      label_es: "",
      label_zh: "",
      required: false,
      options: [],
      sensitive_tags: [],
      section_en: "",
      section_es: "",
      section_zh: "",
    }],
  }));
  const removeQuestion = (idx) => setQ((prev) => ({ ...prev, questions: prev.questions.filter((_, i) => i !== idx) }));
  const moveQuestion = (idx, dir) => setQ((prev) => {
    const arr = [...prev.questions];
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return prev;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    return { ...prev, questions: arr };
  });

  const save = async () => {
    // Builder safety catch: any choice question that ships without options
    // would render as a fallback warning (or, before the QA fix, a free-text
    // input). Block save and surface a clear admin warning instead.
    const missingOptIdx = q.questions.findIndex(
      (qq) =>
        ["single_choice", "multi_choice", "multiple_choice", "checkbox", "radio"].includes(
          (qq?.type || "").toLowerCase(),
        ) && !((qq?.options || []).length > 0),
    );
    if (missingOptIdx !== -1) {
      toast.error(t("admin.builderMissingOptions").replace("{n}", String(missingOptIdx + 1)));
      return;
    }
    setSaving(true);
    try {
      const payload = { ...q };
      if (isNew) {
        const { data } = await api.post("/admin/questionnaires", payload);
        toast.success(t("common.saved"));
        navigate(`/admin/questionnaires/${data.id}`);
      } else {
        await api.put(`/admin/questionnaires/${id}`, payload);
        toast.success(t("common.saved"));
      }
    } catch (e) { toast.error(extractErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const askConfirm = (kind) => {
    if (kind === "delete") {
      const reasonKey = deleteBlockReasonKey(q, responseCount);
      if (reasonKey) {
        toast.error(t(reasonKey));
        return;
      }
    }
    setConfirm(kind);
  };

  const runConfirm = async () => {
    if (!confirm) return;
    setBusy(true);
    try {
      if (confirm === "delete") {
        await api.delete(`/admin/questionnaires/${id}`);
        toast.success(t("admin.deleteSuccess"));
        navigate("/admin/questionnaires");
        return;
      }
      if (confirm === "archive") {
        await api.post(`/admin/questionnaires/${id}/archive`);
        toast.success(t("admin.archiveSuccess"));
        navigate("/admin/questionnaires");
        return;
      }
      if (confirm === "restore") {
        await api.post(`/admin/questionnaires/${id}/restore`);
        toast.success(t("admin.restoreSuccess"));
        setQ((prev) => ({ ...prev, status: "active" }));
        setConfirm(null);
        return;
      }
    } catch (e) {
      toast.error(extractErrorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const formatTpl = (tpl, n) => (tpl || "").replace("{n}", String(n));
  const archived = q.status === "archived";
  const deleteBlockKey = deleteBlockReasonKey(q, responseCount);
  const canDelete = !isNew && !archived && !deleteBlockKey;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="tsw-overline">{t("admin.builder")}</div>
          <h1 className="font-display font-bold text-3xl text-slate-900">{isNew ? t("admin.newQuestionnaire") : t("common.edit")}</h1>
          {!isNew && (
            <div className="text-xs text-slate-500 mt-1 flex flex-wrap items-center gap-1.5" data-testid="builder-response-count">
              {responseCount === 0
                ? t("admin.noResponses")
                : formatTpl(t("admin.hasResponses"), responseCount)}
              {archived && <span className="tsw-chip bg-rose-50 text-rose-700 border border-rose-200">{t("admin.archived")}</span>}
              {isOfficialQ(q) && (
                <span className="tsw-chip bg-emerald-50 text-emerald-800 border border-emerald-300" data-testid="builder-official-badge" title={t("admin.officialProtected")}>
                  <ShieldCheck className="w-3 h-3 inline" /> {t("admin.classificationOfficial")}
                </span>
              )}
              {isDemoQ(q) && !archived && (
                <span className="tsw-chip bg-violet-50 text-violet-700 border border-violet-200">{t("admin.classificationDemo")}</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {!isNew && !archived && (
            <>
              {/* Delete: ALWAYS visible, disabled when protected */}
              <button
                onClick={() => canDelete && askConfirm("delete")}
                disabled={!canDelete}
                title={deleteBlockKey ? t(deleteBlockKey) : t("admin.deleteQuestionnaire")}
                className={`tsw-btn-secondary ${canDelete ? "text-rose-600 hover:bg-rose-50" : "opacity-50 cursor-not-allowed"}`}
                data-testid="builder-delete"
              >
                <Trash2 className="w-4 h-4" /> {t("admin.deleteQuestionnaire")}
              </button>
              {/* Archive shown alongside Delete whenever delete is blocked */}
              {!canDelete && (
                <button
                  onClick={() => askConfirm("archive")}
                  className="tsw-btn-secondary text-amber-700"
                  data-testid="builder-archive"
                >
                  <Archive className="w-4 h-4" /> {t("admin.archive")}
                </button>
              )}
            </>
          )}
          {!isNew && archived && (
            <button
              onClick={() => askConfirm("restore")}
              className="tsw-btn-secondary"
              data-testid="builder-restore"
            >
              <ArchiveRestore className="w-4 h-4" /> {t("admin.restore")}
            </button>
          )}
          <button onClick={save} className="tsw-btn-primary" disabled={saving || archived} data-testid="builder-save">
            <Save className="w-4 h-4" /> {saving ? t("common.saving") : t("admin.saveQuestionnaire")}
          </button>
        </div>
      </div>

      <div className="tsw-card p-5 space-y-4">
        {!isNew && (
          <div className="space-y-3" data-testid="builder-i18n-section">
            {!canWriteI18n && (
              <div
                data-testid="builder-i18n-readonly-banner"
                className="rounded-md border border-slate-300 bg-slate-50 px-3 py-2 text-xs text-slate-700"
              >
                <strong>{i18nReadOnlyReason}</strong> — chips are visible for inspection only.
                <span data-testid="builder-i18n-readonly-banner-base-edit-note"> Base-text edit (PATCH BASE) is hidden for this role.</span>
                {" "}Save button (PUT) is also blocked for non-admin roles by backend RBAC.
              </div>
            )}
            <LanguageTabs
              summary={summary}
              activeLang={activeLang}
              onChange={setActiveLang}
              highlightedLangs={recentCascade.langs}
            />
            {activeLangBlockedNoScaffold && (
              <div
                data-testid="builder-i18n-no-scaffold-banner"
                className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
              >
                This questionnaire is missing the i18n scaffold for non-base languages.
                Inputs below remain editable but will be saved without a tracked translation_status entry.
                The scaffold initializer endpoint will land in sub-sprint 2.0-A-S3.
              </div>
            )}
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input
                type="checkbox"
                data-testid="builder-i18n-show-all-base"
                checked={showAllBase}
                onChange={(e) => setShowAllBase(e.target.checked)}
              />
              Show all base languages (EN / ES / ZH) stacked
            </label>
          </div>
        )}
        {showAllBase || isNew ? (
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="tsw-label">Title (EN)</label>
              <input className="tsw-input" value={q.title_en} onChange={(e) => update({ title_en: e.target.value })} data-testid="builder-title-en" />
            </div>
            <div>
              <label className="tsw-label">Title (ES)</label>
              <input className="tsw-input" value={q.title_es} onChange={(e) => update({ title_es: e.target.value })} data-testid="builder-title-es" />
            </div>
            <div>
              <label className="tsw-label">标题 (ZH 草稿)</label>
              <input className="tsw-input" value={q.title_zh || ""} onChange={(e) => update({ title_zh: e.target.value })} data-testid="builder-title-zh" />
            </div>
            <div>
              <label className="tsw-label">Description (EN)</label>
              <textarea className="tsw-input" value={q.description_en} onChange={(e) => update({ description_en: e.target.value })} />
            </div>
            <div>
              <label className="tsw-label">Description (ES)</label>
              <textarea className="tsw-input" value={q.description_es} onChange={(e) => update({ description_es: e.target.value })} />
            </div>
            <div>
              <label className="tsw-label">说明 (ZH 草稿)</label>
              <textarea className="tsw-input" value={q.description_zh || ""} onChange={(e) => update({ description_zh: e.target.value })} />
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="builder-i18n-active-fields">
            <div>
              <label className="tsw-label flex items-center gap-2">
                <span>Title</span>
                <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase">{activeLang}</span>
                {activeIsRtl && <span className="rounded bg-purple-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase">rtl</span>}
              </label>
              <div className="flex items-center gap-2">
                <input
                  key={`title-${activeLang}`}
                  className="tsw-input flex-1"
                  dir={activeIsRtl ? "rtl" : "ltr"}
                  value={q[fieldFor("title", activeLang)] || ""}
                  onChange={(e) => update({ [fieldFor("title", activeLang)]: e.target.value })}
                  data-testid={`builder-title-${activeLang}`}
                />
                <StatusChip
                  status={statusFor(i18nTs, pathFor({ field: "title", lang: activeLang }), activeIsBase)}
                  path={pathFor({ field: "title", lang: activeLang })}
                  langIsBase={activeIsBase}
                  hasScaffold={activeHasScaffold || activeIsBase}
                  value={q[fieldFor("title", activeLang)] || ""}
                  busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "title", lang: activeLang }))}
                  onClick={(next) => handleChipClick(next, {
                    langDisplay: activeLang.toUpperCase(),
                    value: q[fieldFor("title", activeLang)] || "",
                  })}
                />
                {activeIsBase && canWriteI18n && (
                  <BaseEditButton
                    path={pathFor({ field: "title", lang: activeLang })}
                    disabled={!canWriteI18n}
                    disabledReason={i18nReadOnlyReason}
                    onClick={(p) => openBaseEdit(p, q[fieldFor("title", activeLang)] || "")}
                  />
                )}
              </div>
            </div>
            <div>
              <label className="tsw-label flex items-center gap-2">
                <span>Description</span>
                <span className="rounded bg-slate-200 px-1.5 py-0.5 text-[10px] font-semibold uppercase">{activeLang}</span>
              </label>
              <div className="flex items-start gap-2">
                <textarea
                  key={`desc-${activeLang}`}
                  className="tsw-input flex-1"
                  dir={activeIsRtl ? "rtl" : "ltr"}
                  value={q[fieldFor("description", activeLang)] || ""}
                  onChange={(e) => update({ [fieldFor("description", activeLang)]: e.target.value })}
                  data-testid={`builder-description-${activeLang}`}
                />
                <StatusChip
                  status={statusFor(i18nTs, pathFor({ field: "description", lang: activeLang }), activeIsBase)}
                  path={pathFor({ field: "description", lang: activeLang })}
                  langIsBase={activeIsBase}
                  hasScaffold={activeHasScaffold || activeIsBase}
                  value={q[fieldFor("description", activeLang)] || ""}
                  busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "description", lang: activeLang }))}
                  onClick={(next) => handleChipClick(next, {
                    langDisplay: activeLang.toUpperCase(),
                    value: q[fieldFor("description", activeLang)] || "",
                  })}
                />
                {activeIsBase && canWriteI18n && (
                  <BaseEditButton
                    path={pathFor({ field: "description", lang: activeLang })}
                    disabled={!canWriteI18n}
                    disabledReason={i18nReadOnlyReason}
                    onClick={(p) => openBaseEdit(p, q[fieldFor("description", activeLang)] || "")}
                  />
                )}
              </div>
            </div>
          </div>
        )}
        <div>
          <label className="tsw-label">Profiles</label>
          <div className="flex flex-wrap gap-2" data-testid="builder-profiles">
            {profiles.map((p) => {
              const checked = q.profile_keys.includes(p.key);
              return (
                <label key={p.key} className={`tsw-chip cursor-pointer ${checked ? "bg-emerald-600 text-white" : "bg-slate-100 text-slate-700"}`}>
                  <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => {
                    update({ profile_keys: e.target.checked ? [...q.profile_keys, p.key] : q.profile_keys.filter((k) => k !== p.key) });
                  }} />
                  {p.label_en}
                </label>
              );
            })}
          </div>
        </div>
        <div className="flex flex-wrap gap-4 pt-2 border-t border-slate-100">
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={q.is_internal} onChange={(e) => update({ is_internal: e.target.checked, profile_keys: e.target.checked ? [] : q.profile_keys })} data-testid="builder-internal" />
            Internal interviewer-only
          </label>
          <label className="text-sm flex items-center gap-2">
            <input type="checkbox" checked={q.is_active} onChange={(e) => update({ is_active: e.target.checked })} />
            Active
          </label>
        </div>
      </div>

      <div className="space-y-4">
        {q.questions.map((qq, idx) => (
          <div key={qq.id} className="tsw-card p-5 space-y-3" data-testid={`builder-q-${idx}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <GripVertical className="w-4 h-4 text-slate-400" />
                <span className="font-semibold text-slate-700 text-sm">#{idx + 1}</span>
                <select className="tsw-input py-1 text-sm" value={qq.type} onChange={(e) => updateQuestion(idx, { type: e.target.value })} data-testid={`builder-q-type-${idx}`}>
                  {QTYPES.map((tp) => <option key={tp.v} value={tp.v}>{tp.label}</option>)}
                </select>
                <label className="text-sm flex items-center gap-1">
                  <input type="checkbox" checked={!!qq.required} onChange={(e) => updateQuestion(idx, { required: e.target.checked })} /> required
                </label>
              </div>
              <div className="flex items-center gap-2">
                <button className="tsw-btn-ghost text-xs" onClick={() => moveQuestion(idx, -1)}>↑</button>
                <button className="tsw-btn-ghost text-xs" onClick={() => moveQuestion(idx, 1)}>↓</button>
                <button onClick={() => removeQuestion(idx)} className="text-rose-600 hover:bg-rose-50 rounded-full p-2" data-testid={`builder-q-remove-${idx}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
            {(showAllBase || isNew) ? (
              <div className="grid sm:grid-cols-2 gap-3">
                <input className="tsw-input" placeholder="Question (EN)" value={qq.label_en} onChange={(e) => updateQuestion(idx, { label_en: e.target.value })} data-testid={`builder-q-label-en-${idx}`} />
                <input className="tsw-input" placeholder="Pregunta (ES)" value={qq.label_es} onChange={(e) => updateQuestion(idx, { label_es: e.target.value })} data-testid={`builder-q-label-es-${idx}`} />
                <input className="tsw-input" placeholder="问题 (ZH 草稿)" value={qq.label_zh || ""} onChange={(e) => updateQuestion(idx, { label_zh: e.target.value })} data-testid={`builder-q-label-zh-${idx}`} />
                <input className="tsw-input" placeholder="Section (EN)" value={qq.section_en || ""} onChange={(e) => updateQuestion(idx, { section_en: e.target.value })} />
                <input className="tsw-input" placeholder="Sección (ES)" value={qq.section_es || ""} onChange={(e) => updateQuestion(idx, { section_es: e.target.value })} />
                <input className="tsw-input" placeholder="章节 (ZH 草稿)" value={qq.section_zh || ""} onChange={(e) => updateQuestion(idx, { section_zh: e.target.value })} />
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">
                <div className="flex items-center gap-2 sm:col-span-1">
                  <input
                    key={`q-label-${activeLang}-${idx}`}
                    className="tsw-input flex-1"
                    dir={activeIsRtl ? "rtl" : "ltr"}
                    placeholder={`Question (${activeLang.toUpperCase()})`}
                    value={qq[fieldFor("label", activeLang)] || ""}
                    onChange={(e) => updateQuestion(idx, { [fieldFor("label", activeLang)]: e.target.value })}
                    data-testid={`builder-q-label-${activeLang}-${idx}`}
                  />
                  <StatusChip
                    status={statusFor(i18nTs, pathFor({ field: "label", questionId: qq.id, lang: activeLang }), activeIsBase)}
                    path={pathFor({ field: "label", questionId: qq.id, lang: activeLang })}
                    langIsBase={activeIsBase}
                    hasScaffold={activeHasScaffold || activeIsBase}
                    value={qq[fieldFor("label", activeLang)] || ""}
                    busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "label", questionId: qq.id, lang: activeLang }))}
                    onClick={(next) => handleChipClick(next, {
                      langDisplay: activeLang.toUpperCase(),
                      value: qq[fieldFor("label", activeLang)] || "",
                    })}
                  />
                  {activeIsBase && canWriteI18n && (
                    <BaseEditButton
                      path={pathFor({ field: "label", questionId: qq.id, lang: activeLang })}
                      disabled={!canWriteI18n}
                      disabledReason={i18nReadOnlyReason}
                      onClick={(p) => openBaseEdit(p, qq[fieldFor("label", activeLang)] || "")}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 sm:col-span-1">
                  <input
                    key={`q-help-${activeLang}-${idx}`}
                    className="tsw-input flex-1"
                    dir={activeIsRtl ? "rtl" : "ltr"}
                    placeholder={`Help text (${activeLang.toUpperCase()})`}
                    value={qq[fieldFor("help", activeLang)] || ""}
                    onChange={(e) => updateQuestion(idx, { [fieldFor("help", activeLang)]: e.target.value })}
                    data-testid={`builder-q-help-${activeLang}-${idx}`}
                  />
                  <StatusChip
                    status={statusFor(i18nTs, pathFor({ field: "help", questionId: qq.id, lang: activeLang }), activeIsBase)}
                    path={pathFor({ field: "help", questionId: qq.id, lang: activeLang })}
                    langIsBase={activeIsBase}
                    hasScaffold={activeHasScaffold || activeIsBase}
                    value={qq[fieldFor("help", activeLang)] || ""}
                    busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "help", questionId: qq.id, lang: activeLang }))}
                    onClick={(next) => handleChipClick(next, {
                      langDisplay: activeLang.toUpperCase(),
                      value: qq[fieldFor("help", activeLang)] || "",
                    })}
                  />
                  {activeIsBase && canWriteI18n && (
                    <BaseEditButton
                      path={pathFor({ field: "help", questionId: qq.id, lang: activeLang })}
                      disabled={!canWriteI18n}
                      disabledReason={i18nReadOnlyReason}
                      onClick={(p) => openBaseEdit(p, qq[fieldFor("help", activeLang)] || "")}
                    />
                  )}
                </div>
                <div className="flex items-center gap-2 sm:col-span-2">
                  <input
                    key={`q-section-${activeLang}-${idx}`}
                    className="tsw-input flex-1"
                    dir={activeIsRtl ? "rtl" : "ltr"}
                    placeholder={`Section (${activeLang.toUpperCase()})`}
                    value={qq[fieldFor("section", activeLang)] || ""}
                    onChange={(e) => updateQuestion(idx, { [fieldFor("section", activeLang)]: e.target.value })}
                    data-testid={`builder-q-section-${activeLang}-${idx}`}
                  />
                  <StatusChip
                    status={statusFor(i18nTs, pathFor({ field: "section", questionId: qq.id, lang: activeLang }), activeIsBase)}
                    path={pathFor({ field: "section", questionId: qq.id, lang: activeLang })}
                    langIsBase={activeIsBase}
                    hasScaffold={activeHasScaffold || activeIsBase}
                    value={qq[fieldFor("section", activeLang)] || ""}
                    busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "section", questionId: qq.id, lang: activeLang }))}
                    onClick={(next) => handleChipClick(next, {
                      langDisplay: activeLang.toUpperCase(),
                      value: qq[fieldFor("section", activeLang)] || "",
                    })}
                  />
                  {activeIsBase && canWriteI18n && (
                    <BaseEditButton
                      path={pathFor({ field: "section", questionId: qq.id, lang: activeLang })}
                      disabled={!canWriteI18n}
                      disabledReason={i18nReadOnlyReason}
                      onClick={(p) => openBaseEdit(p, qq[fieldFor("section", activeLang)] || "")}
                    />
                  )}
                </div>
              </div>
            )}

            {(qq.type === "single_choice" || qq.type === "multi_choice") && (
              <div className="space-y-2">
                <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Options · EN / ES / ZH</div>
                {(qq.options || []).length === 0 && (
                  <div
                    className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
                    data-testid={`builder-q-missing-options-${idx}`}
                  >
                    {t("admin.builderMissingOptionsInline")}
                  </div>
                )}
                {(qq.options || []).map((opt, oi) => (
                  <div key={opt._uid || `${idx}-${oi}-${opt.value}`} className="grid grid-cols-9 gap-2 items-center">
                    <input className="tsw-input col-span-2" placeholder="value" value={opt.value} onChange={(e) => updateQuestion(idx, { options: qq.options.map((o, i) => i === oi ? { ...o, value: e.target.value } : o) })} />
                    {(showAllBase || isNew) ? (
                      <>
                        <input className="tsw-input col-span-2" placeholder="EN" value={opt.label_en} onChange={(e) => updateQuestion(idx, { options: qq.options.map((o, i) => i === oi ? { ...o, label_en: e.target.value } : o) })} />
                        <input className="tsw-input col-span-2" placeholder="ES" value={opt.label_es} onChange={(e) => updateQuestion(idx, { options: qq.options.map((o, i) => i === oi ? { ...o, label_es: e.target.value } : o) })} />
                        <input className="tsw-input col-span-2" placeholder="ZH" value={opt.label_zh || ""} onChange={(e) => updateQuestion(idx, { options: qq.options.map((o, i) => i === oi ? { ...o, label_zh: e.target.value } : o) })} />
                      </>
                    ) : (
                      <>
                        <input
                          key={`opt-${activeLang}-${idx}-${oi}`}
                          className="tsw-input col-span-5"
                          dir={activeIsRtl ? "rtl" : "ltr"}
                          placeholder={`Label (${activeLang.toUpperCase()})`}
                          value={opt[fieldFor("label", activeLang)] || ""}
                          onChange={(e) => updateQuestion(idx, {
                            options: qq.options.map((o, i) => i === oi ? { ...o, [fieldFor("label", activeLang)]: e.target.value } : o),
                          })}
                          data-testid={`builder-q-option-${activeLang}-${idx}-${oi}`}
                        />
                        <div className="col-span-1 flex items-center justify-center">
                          <StatusChip
                            status={statusFor(i18nTs, pathFor({ field: "label", questionId: qq.id, optionValue: opt.value, lang: activeLang }), activeIsBase)}
                            path={pathFor({ field: "label", questionId: qq.id, optionValue: opt.value, lang: activeLang })}
                            langIsBase={activeIsBase}
                            hasScaffold={activeHasScaffold || activeIsBase}
                            value={opt[fieldFor("label", activeLang)] || ""}
                            busy={patchBusy}
                  disabled={!canWriteI18n}
                  highlight={recentCascade.paths.has(pathFor({ field: "label", questionId: qq.id, optionValue: opt.value, lang: activeLang }))}
                            onClick={(next) => handleChipClick(next, {
                              langDisplay: activeLang.toUpperCase(),
                              value: opt[fieldFor("label", activeLang)] || "",
                            })}
                          />
                          {activeIsBase && canWriteI18n && (
                            <BaseEditButton
                              path={pathFor({ field: "label", questionId: qq.id, optionValue: opt.value, lang: activeLang })}
                              disabled={!canWriteI18n}
                              disabledReason={i18nReadOnlyReason}
                              compact
                              onClick={(p) => openBaseEdit(p, opt[fieldFor("label", activeLang)] || "")}
                            />
                          )}
                        </div>
                      </>
                    )}
                    <button onClick={() => updateQuestion(idx, { options: qq.options.filter((_, i) => i !== oi) })} className="text-rose-600"><Trash2 className="w-4 h-4" /></button>
                  </div>
                ))}
                <button onClick={() => updateQuestion(idx, { options: [...(qq.options || []), { _uid: crypto.randomUUID(), value: "", label_en: "", label_es: "", label_zh: "" }] })} className="tsw-btn-ghost text-xs">+ option</button>
              </div>
            )}

            <div>
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-widest">Sensitive tags (research-only)</div>
              <div className="flex flex-wrap gap-2 mt-1.5">
                {SENSITIVE.map((tag) => {
                  const checked = (qq.sensitive_tags || []).includes(tag);
                  return (
                    <label key={tag} className={`tsw-chip cursor-pointer ${checked ? "bg-amber-100 text-amber-800 border border-amber-200" : "bg-slate-100 text-slate-700"}`}>
                      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => updateQuestion(idx, {
                        sensitive_tags: e.target.checked ? [...(qq.sensitive_tags || []), tag] : (qq.sensitive_tags || []).filter((x) => x !== tag),
                      })} />
                      {tag}
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        ))}
        <button onClick={addQuestion} className="tsw-btn-secondary" data-testid="builder-add-question">
          <Plus className="w-4 h-4" /> {t("admin.addQuestion")}
        </button>
      </div>

      <div className="text-xs text-slate-500 border-t border-slate-100 pt-4">
        {t("admin.consentNote")}
      </div>

      {!isNew && (
        <TranslationSummaryWidget
          summary={summary}
          loading={summaryLoading}
          error={summaryError}
          onReload={loadSummary}
          onJumpToLang={setActiveLang}
          highlightedLangs={recentCascade.langs}
        />
      )}

      {/* QUESTIONNAIRE-2.0-A-S2-β-B — T1 confirm dialog (promote/demote reviewed) */}
      {t1Dialog && (
        <T1ConfirmReviewedDialog
          open={!!t1Dialog}
          mode={t1Dialog.mode}
          field={t1Dialog.field}
          fromStatus={t1Dialog.fromStatus}
          busy={t1Busy || patchBusy}
          errorMessage={t1Error}
          onCancel={() => { setT1Dialog(null); setT1Error(null); }}
          onConfirm={handleT1Confirm}
        />
      )}

      {/* QUESTIONNAIRE-2.0-A-S2-γ-D-B — Conflict diff modal (value flow) */}
      {conflict && (
        <ConflictDiffModal
          key={`conflict-${conflict.attempt?.path || "unknown"}`}
          open={!!conflict}
          mode={conflict.mode}
          attempt={conflict.attempt}
          conflictPayload={conflict.payload}
          remoteSnapshot={conflict.remoteSnapshot}
          busy={conflictBusy || patchBusy}
          onReload={handleConflictReload}
          onCancel={handleConflictCancel}
          onCopyAttempt={handleConflictCopyAttempt}
          onDiscard={handleConflictDiscard}
          onRetry={handleConflictRetry}
        />
      )}

      {/* QUESTIONNAIRE-2.0-A-S2-γ-B — Base-edit dialog (PATCH value for EN/ES/ZH fields) */}
      {baseEditDialog && (
        <BaseEditDialog
          key={`base-edit-${baseEditDialog.path}`}
          open={!!baseEditDialog}
          path={baseEditDialog.path}
          currentValue={baseEditDialog.currentValue}
          summary={summary}
          translation_status={i18nTs}
          busy={patchBusy}
          error={baseEditError}
          onCancel={closeBaseEdit}
          onConfirm={handleBaseEditConfirm}
        />
      )}

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="builder-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={`w-5 h-5 ${confirm === "delete" ? "text-rose-600" : confirm === "archive" ? "text-amber-600" : "text-emerald-600"}`} />
              {confirm === "delete" && t("admin.confirmDeleteTitle")}
              {confirm === "archive" && t("admin.confirmArchiveTitle")}
              {confirm === "restore" && t("admin.confirmRestoreTitle")}
            </DialogTitle>
            <DialogDescription className="pt-2 text-slate-600">
              {confirm === "delete" && t("admin.confirmDeleteBody")}
              {confirm === "archive" && formatTpl(t("admin.confirmArchiveBody"), responseCount)}
              {confirm === "restore" && t("admin.confirmRestoreBody")}
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-sm text-slate-700 space-y-1.5" data-testid="builder-confirm-preview">
            <div className="font-semibold text-slate-900">{q.title_en || t("admin.newQuestionnaire")}</div>
            <div className="flex flex-wrap gap-2 text-xs">
              <span className="tsw-chip bg-white border border-slate-200">
                <span className="text-slate-500">{t("admin.classification")}:</span>&nbsp;
                <span className={`font-semibold ${isOfficialQ(q) ? "text-emerald-700" : isDemoQ(q) ? "text-violet-700" : q.is_internal ? "text-amber-700" : "text-slate-700"}`} data-testid="builder-confirm-classification">
                  {t(classificationKeyQ(q))}
                </span>
              </span>
              <span className="tsw-chip bg-white border border-slate-200">
                <span className="text-slate-500">{t("admin.statusLabel")}:</span>&nbsp;
                <span className="font-semibold text-slate-900" data-testid="builder-confirm-status">{t(statusKeyQ(q))}</span>
              </span>
              <span className="tsw-chip bg-white border border-slate-200" data-testid="builder-confirm-response-count">
                <span className="text-slate-500">{t("admin.responses")}:</span>&nbsp;
                <span className={`font-semibold ${responseCount > 0 ? "text-amber-700" : "text-slate-900"}`}>{responseCount}</span>
              </span>
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <button onClick={() => setConfirm(null)} className="tsw-btn-secondary" disabled={busy} data-testid="builder-confirm-cancel">
              {t("common.cancel")}
            </button>
            <button
              onClick={runConfirm}
              disabled={busy}
              data-testid="builder-confirm-ok"
              className={
                confirm === "delete"
                  ? "tsw-btn-primary !bg-rose-600 hover:!bg-rose-700"
                  : confirm === "archive"
                    ? "tsw-btn-primary !bg-amber-600 hover:!bg-amber-700"
                    : "tsw-btn-primary"
              }
            >
              {busy
                ? t("common.saving")
                : confirm === "delete"
                  ? t("admin.deleteQuestionnaire")
                  : confirm === "archive"
                    ? t("admin.archive")
                    : t("admin.restore")}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default QuestionnaireBuilder;
